import { useCallback, useEffect, useRef, useState } from "react";

import {
  DEFAULT_MAX_AUDIO_SECONDS,
  buildRecordedVoiceMessageFile,
  isVoiceRecordingSupported
} from "./audio";

interface RecordedVoiceMessage {
  file: File;
  durationSeconds: number;
}

interface UsePressAndHoldRecorderOptions {
  disabled?: boolean;
  maxDurationSeconds?: number;
  onRecorded: (message: RecordedVoiceMessage) => Promise<void> | void;
  onError?: (message: string) => void;
}

function resolveRecordingErrorMessage(error: unknown) {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
      return "Autorisez le micro pour envoyer un message vocal.";
    }

    if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
      return "Aucun microphone n'a ete detecte sur cet appareil.";
    }
  }

  return error instanceof Error ? error.message : "Impossible d'enregistrer ce message vocal.";
}

export function usePressAndHoldRecorder({
  disabled = false,
  maxDurationSeconds = DEFAULT_MAX_AUDIO_SECONDS,
  onRecorded,
  onError
}: UsePressAndHoldRecorderOptions) {
  const [isPreparing, setIsPreparing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [durationSeconds, setDurationSeconds] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<number | null>(null);
  const autoStopRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const mountedRef = useRef(true);
  const shouldDiscardRef = useRef(false);
  const onRecordedRef = useRef(onRecorded);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onRecordedRef.current = onRecorded;
  }, [onRecorded]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const reportError = useCallback((message: string) => {
    onErrorRef.current?.(message);
  }, []);

  const stopTimers = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (autoStopRef.current !== null) {
      window.clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    const stream = streamRef.current ?? mediaRecorderRef.current?.stream ?? null;

    if (!stream) {
      return;
    }

    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        // Ignore track shutdown errors.
      }
    });

    streamRef.current = null;
  }, []);

  const resetVisualState = useCallback(() => {
    stopTimers();

    if (mountedRef.current) {
      setIsPreparing(false);
      setIsRecording(false);
      setDurationSeconds(0);
    }
  }, [stopTimers]);

  const cleanupRecorder = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.onerror = null;
    }

    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  }, []);

  const updateTimer = useCallback(() => {
    const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
    setDurationSeconds(Math.max(0, elapsed));
  }, []);

  const startRecording = useCallback(async () => {
    if (disabled || isPreparing || isRecording) {
      return;
    }

    if (!isVoiceRecordingSupported()) {
      reportError("L'enregistrement vocal n'est pas pris en charge sur cet appareil.");
      return;
    }

    cleanupRecorder();
    stopStream();
    stopTimers();
    audioChunksRef.current = [];
    shouldDiscardRef.current = false;
    setDurationSeconds(0);
    setIsPreparing(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);

      streamRef.current = stream;
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        stopStream();
        cleanupRecorder();
        resetVisualState();
        reportError("Impossible d'enregistrer ce message vocal.");
      };

      recorder.onstop = () => {
        const shouldDiscard = shouldDiscardRef.current;
        const audioBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const measuredDurationSeconds = Math.max(1, Math.ceil((Date.now() - startedAtRef.current) / 1000));

        stopStream();
        stopTimers();
        cleanupRecorder();

        if (shouldDiscard) {
          resetVisualState();
          return;
        }

        setIsRecording(false);
        setIsPreparing(false);
        setDurationSeconds(0);
        stopTimers();

        void (async () => {
          if (!audioBlob.size) {
            resetVisualState();
            reportError("Le message vocal est vide. Réessayez en parlant un peu plus longtemps.");
            return;
          }

          try {
            const voiceMessage = await buildRecordedVoiceMessageFile(audioBlob, maxDurationSeconds, measuredDurationSeconds);
            resetVisualState();
            await onRecordedRef.current(voiceMessage);
          } catch (error) {
            resetVisualState();
            reportError(resolveRecordingErrorMessage(error));
          }
        })();
      };

      recorder.start();
      startedAtRef.current = Date.now();
      setIsPreparing(false);
      setIsRecording(true);
      setDurationSeconds(0);

      timerRef.current = window.setInterval(updateTimer, 1000);
      autoStopRef.current = window.setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
          stopStream();
        }
      }, maxDurationSeconds * 1000);
    } catch (error) {
      stopStream();
      cleanupRecorder();
      resetVisualState();
      reportError(resolveRecordingErrorMessage(error));
    }
  }, [
    cleanupRecorder,
    disabled,
    isPreparing,
    isRecording,
    maxDurationSeconds,
    reportError,
    resetVisualState,
    stopStream,
    stopTimers,
    updateTimer
  ]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;

    if (!recorder || recorder.state === "inactive") {
      return;
    }

    try {
      recorder.stop();
      stopStream();
    } catch {
      stopStream();
      cleanupRecorder();
      resetVisualState();
      reportError("Impossible d'arrêter ce vocal. Réessayez.");
    }
  }, [cleanupRecorder, reportError, resetVisualState, stopStream]);

  const cancelRecording = useCallback(() => {
    shouldDiscardRef.current = true;

    const recorder = mediaRecorderRef.current;

    if (!recorder || recorder.state === "inactive") {
      stopStream();
      cleanupRecorder();
      resetVisualState();
      return;
    }

    try {
      recorder.stop();
      stopStream();
    } catch {
      stopStream();
      cleanupRecorder();
      resetVisualState();
    }
  }, [cleanupRecorder, resetVisualState, stopStream]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      shouldDiscardRef.current = true;

      const recorder = mediaRecorderRef.current;

      try {
        if (recorder && recorder.state !== "inactive") {
          recorder.stop();
        }
      } catch {
        // Ignore unmount errors.
      }

      stopStream();
      cleanupRecorder();
      stopTimers();
    };
  }, [cleanupRecorder, stopStream, stopTimers]);

  return {
    isSupported: isVoiceRecordingSupported(),
    isPreparing,
    isRecording,
    isProcessing: false,
    isBusy: isPreparing || isRecording,
    durationSeconds,
    startRecording,
    stopRecording,
    cancelRecording
  };
}
